import { useFormat } from "@/i18n/format";
import { useT } from "@/i18n/LocaleProvider";

/**
 * One budget's progress (`project.md` §5.3).
 *
 * Bars are the single most-praised element in the reference apps' reviews, and
 * they are cheap: a track, a fill, and two numbers. What makes this one worth
 * writing down is the colour rule.
 *
 * **Red is reserved strictly for overspend** (`project.md` §5.5). Not "nearly
 * there", not "expense", not "negative". Every expense in this product is a
 * negative number, so colouring by sign would paint the whole screen red and
 * leave the one figure that should alarm somebody indistinguishable from the
 * forty that should not. `is_overspent` is decided by the server — it is
 * `spent > budget`, one minor unit past the limit — and this component asks
 * nothing else.
 *
 * The fill stops at 100% because a bar cannot show 180% of itself, and the
 * overshoot is stated in words underneath instead, where it can be read.
 */
export function BudgetBar({
  spentMinor,
  budgetMinor,
  currency,
  ratio,
  isOverspent,
  label,
}: {
  spentMinor: number;
  budgetMinor: number;
  currency: string;
  ratio: number;
  isOverspent: boolean;
  label: string;
}) {
  const t = useT();
  const format = useFormat();
  const filled = Math.max(0, Math.min(ratio, 1)) * 100;
  const remaining = budgetMinor - spentMinor;

  return (
    <div>
      <div className="flex items-baseline justify-between gap-4 text-sm">
        <span className="truncate">{label}</span>
        <span className="num shrink-0 text-muted">
          {format.amount(spentMinor, currency)} / {format.amount(budgetMinor, currency)}
        </span>
      </div>

      <div
        role="progressbar"
        aria-label={t("budget.barLabel", {
          label,
          spent: format.amount(spentMinor, currency),
          budget: format.amount(budgetMinor, currency),
        })}
        aria-valuemin={0}
        aria-valuemax={budgetMinor}
        aria-valuenow={spentMinor}
        className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-track"
      >
        <div
          // No gradient: project.md §5.5 keeps financial figures flat.
          className={`h-full rounded-full ${isOverspent ? "bg-overspend" : "bg-accent"}`}
          style={{ width: `${filled}%` }}
        />
      </div>

      <p
        className={`mt-1 text-xs ${isOverspent ? "text-overspend" : "text-muted"}`}
        data-testid="budget-remaining"
      >
        {isOverspent
          ? t("budget.overspentBy", { amount: format.amount(-remaining, currency) })
          : t("budget.remaining", { amount: format.amount(remaining, currency) })}
      </p>
    </div>
  );
}
